import React, { useEffect, useState } from 'react';
import {
  getAdminCategories,
  createCategory,
  updateCategory,
  deleteCategory,
} from '../../services/adminApi';
import {
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineX,
} from 'react-icons/hi';

interface Category {
  id: number;
  name: string;
  slug: string;
  description: string;
  image: string;
}

const emptyForm = { name: '', slug: '', description: '', image: '' };

const AdminCategories: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const fetchData = async () => {
    try {
      const res = await getAdminCategories();
      const data = Array.isArray(res.data) ? res.data : res.data.results || [];
      setCategories(data);
    } catch {
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowModal(true);
    setError('');
  };

  const openEdit = (c: Category) => {
    setEditingId(c.id);
    setForm({ name: c.name, slug: c.slug, description: c.description, image: c.image || '' });
    setShowModal(true);
    setError('');
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await deleteCategory(id);
      setCategories((prev) => prev.filter((c) => c.id !== id));
      setMessage('Category deleted');
      setTimeout(() => setMessage(''), 3000);
    } catch {
      setError('Failed to delete category');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await updateCategory(editingId, form);
        setMessage('Category updated');
      } else {
        await createCategory(form);
        setMessage('Category created');
      }
      setShowModal(false);
      fetchData();
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      const data = err.response?.data;
      if (data && typeof data === 'object') {
        const msgs = Object.entries(data)
          .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
          .join('; ');
        setError(msgs);
      } else {
        setError('Failed to save category');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  if (loading) return <div className="admin-spinner" />;

  return (
    <div>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Categories</h1>
        <button className="admin-btn admin-btn-primary" onClick={openAdd}>
          <HiOutlinePlus size={18} /> Add Category
        </button>
      </div>

      {message && <div className="admin-alert admin-alert-success">{message}</div>}
      {error && !showModal && <div className="admin-alert admin-alert-error">{error}</div>}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Image</th>
              <th>Name</th>
              <th>Slug</th>
              <th>Description</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {categories.length === 0 ? (
              <tr>
                <td colSpan={5} className="admin-table-empty">No categories found</td>
              </tr>
            ) : (
              categories.map((c) => (
                <tr key={c.id}>
                  <td>
                    {c.image ? (
                      <img src={c.image} alt={c.name} className="admin-table-thumb" />
                    ) : (
                      <div className="admin-table-thumb-placeholder" />
                    )}
                  </td>
                  <td>{c.name}</td>
                  <td><code>{c.slug}</code></td>
                  <td className="admin-td-desc">{c.description}</td>
                  <td>
                    <div className="admin-actions">
                      <button className="admin-btn-icon" onClick={() => openEdit(c)} title="Edit">
                        <HiOutlinePencil size={16} />
                      </button>
                      <button className="admin-btn-icon admin-btn-icon-danger" onClick={() => handleDelete(c.id)} title="Delete">
                        <HiOutlineTrash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="admin-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="admin-modal admin-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{editingId ? 'Edit Category' : 'Add Category'}</h2>
              <button className="admin-btn-icon" onClick={() => setShowModal(false)}>
                <HiOutlineX size={20} />
              </button>
            </div>
            {error && <div className="admin-alert admin-alert-error">{error}</div>}
            <form onSubmit={handleSubmit} className="admin-form">
              <div className="admin-form-group">
                <label>Name</label>
                <input name="name" value={form.name} onChange={handleChange} required />
              </div>
              <div className="admin-form-group">
                <label>Slug</label>
                <input name="slug" value={form.slug} onChange={handleChange} required />
              </div>
              <div className="admin-form-group">
                <label>Image URL</label>
                <input name="image" value={form.image} onChange={handleChange} placeholder="/images/category.jpg" />
              </div>
              <div className="admin-form-group">
                <label>Description</label>
                <textarea name="description" value={form.description} onChange={handleChange} rows={3} />
              </div>
              <div className="admin-form-actions">
                <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCategories;
